<template>
  <div class="entrega-tab-container">
    <!-- Delivered banner -->
    <div v-if="deliveredAt" class="entregue-banner" data-testid="so-item-entregue-banner">
      <i class="pi pi-check-circle"></i>
      <div class="entregue-banner-content">
        <strong>{{ t('Item entregue') }} — {{ formatDateTime(new Date(deliveredAt)) }}</strong>
        <span>{{ t('Não é mais possível avisar o cliente sobre este item.') }}</span>
      </div>
    </div>

    <!-- Delivery Date Editor -->
    <SODeliveryDateEditor
      :order-item-id="itemId"
      :current-expected-delivery-at="currentDeliveryDate"
      :read-only="isReadOnly"
      @updated="handleDeliveryDateUpdated"
    />

    <!-- Pickup Notifications -->
    <SOPickupNotificationsView :notifications="notifications" />

    <!-- Notify button — gone once the item is delivered (the banner says why) -->
    <div v-if="!deliveredAt" class="notify-button-container">
      <Button
        :label="t('Avisar cliente')"
        icon="pi pi-bell"
        size="small"
        severity="info"
        outlined
        data-testid="notify-customer-btn"
        @click="handleNotifyCustomer"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateFormatter } from '../../../composables/useDateFormatter';
import { useSOEntregas } from '../../../composables/useSOEntregas';
import { eventBus } from '../../../eventBus';

const { t } = useI18n();
const { formatDateTime } = useDateFormatter();
const { fetchPickupNotifications } = useSOEntregas();

const props = defineProps<{
  itemId: number;
  isReadOnly?: boolean;
  currentDeliveryDate?: Date | null;
  deliveredAt?: string | null;
}>();

const notifications = ref<unknown[]>([]);

async function loadData() {
  try {
    const [notifResult] = await Promise.all([
      fetchPickupNotifications(props.itemId),
    ]);

    if (notifResult?.notifications) {
      notifications.value = notifResult.notifications;
    }
  } catch (err) {
    // Error handled by composable
  }
}

function handleDeliveryDateUpdated(itemId: number) {
  eventBus.emit('so-order-item.updated', { itemId });
}

function handleNotifyCustomer() {
  eventBus.emit('modal.pdv-entregas-notify.open', { itemId: props.itemId });
}

// Reload the avisos list after a customer notification is sent successfully
// (the notify modal emits the success globalMessage and this event on success).
function handleNotificationsSent() {
  loadData();
}

onMounted(() => {
  eventBus.on('pdv-entregas.notifications.sent', handleNotificationsSent);
});

onBeforeUnmount(() => {
  eventBus.off('pdv-entregas.notifications.sent', handleNotificationsSent);
});

defineExpose({ loadData });
</script>

<style scoped>
.entrega-tab-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.notify-button-container {
  display: flex;
}

.entregue-banner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  background: var(--p-green-50);
  border: 1px solid var(--p-green-200);
  border-radius: 6px;
  color: var(--p-green-800);
  line-height: 1.4;
}

.entregue-banner i {
  font-size: 1rem;
  margin-top: 2px;
  flex-shrink: 0;
}

.entregue-banner-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.85rem;
}
</style>
